/**
 * A call's line in a DM's history (Discord's CALL message, DM calls
 * 2026-11-01): "Bahar started a call." while it runs (with Join call when
 * you are not in it), "Bahar started a call that lasted 5 minutes." after
 * it, and for whoever never joined "You missed a call from Bahar that
 * lasted a few seconds." (red).
 */
import type { Message } from '@neosocial/protocol';
import { useT, type Translator } from '../i18n';
import { callsOn, inCallHere, joinCall, useCalls } from '../store/calls';
import { useData } from '../store/data';
import { userName } from '../store/selectors';
import { Btn, Icon } from '../ui/kit';
import { useVoice } from '../voice/store';
import { openProfile } from './ProfileCard';

/** "a few seconds", "a minute", "5 minutes", "an hour", "2 hours". */
export function callDuration(t: Translator['t'], ms: number): string {
  if (ms < 60_000) return t('call.dur.seconds');
  if (ms < 3_600_000) return t('call.dur.minutes', { n: Math.max(1, Math.round(ms / 60_000)) });
  return t('call.dur.hours', { n: Math.max(1, Math.round(ms / 3_600_000)) });
}

/** Which sentence a viewer reads for this call: running, ended, missed. */
export function callLineKey(
  m: Pick<Message, 'author' | 'call'>,
  meId: string | undefined,
): {
  key: string;
  missed: boolean;
} {
  const ended = m.call?.ended_timestamp;
  if (!ended) return { key: 'call.msg.started', missed: false };
  const joined = !!meId && (m.author.id === meId || !!m.call?.participants.includes(meId));
  return joined
    ? { key: 'call.msg.lasted', missed: false }
    : { key: 'call.msg.missedLasted', missed: true };
}

export function CallMessage({ m }: { m: Message }) {
  const { t, tRich, fmt } = useT();
  const meId = useData((d) => d.me?.id);
  const name = useData((d) => userName(d, m.author.id, undefined));
  // the call still runs (this is its message) and you are not in it: Join
  const running = useCalls((s) => s.calls[m.channel_id]?.messageId === m.id);
  const here = useVoice(() => inCallHere(m.channel_id));
  const { key, missed } = callLineKey(m, meId);
  const ended = m.call?.ended_timestamp;
  const d = ended ? callDuration(t, Date.parse(ended) - Date.parse(m.timestamp)) : '';
  return (
    <article
      className={'msg sys cl-msg' + (missed ? ' missed' : '')}
      data-mid={m.id}
      id={`msg-${m.id}`}
      tabIndex={-1}
    >
      <div className="msg-gut">
        <span className="sys-ic">
          <Icon name={missed ? 'phone-off' : 'phone'} size="s" />
        </span>
      </div>
      <div className="msg-body">
        <p className="sys-t">
          <span>
            {tRich(key, {
              name: (
                <button
                  type="button"
                  className="sys-name"
                  onClick={(e) => openProfile(m.author.id, e.currentTarget)}
                >
                  {name}
                </button>
              ),
              d,
            })}
          </span>{' '}
          <time className="msg-time" dateTime={m.timestamp}>
            {fmt.stamp(m.timestamp)}
          </time>
        </p>
        {running && !ended && !here && callsOn() ? (
          <Btn
            size="xs"
            kind="primary"
            icon="phone"
            className="cl-msg-join"
            onClick={() => void joinCall(m.channel_id)}
          >
            {t('call.join')}
          </Btn>
        ) : null}
      </div>
    </article>
  );
}
